<script>
	import { onMount } from "svelte";
	import { fade } from "svelte/transition";
	import { soundMusicsAll as all, categoryAll, soundSourcesAll } from "../../stores";
	import { Dates } from "../../utils/date";
	import { getResourceUrl } from "../../utils/resource";
	import TableToolbarNoPaging from "../../components/TableToolbarNoPaging.svelte";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import { ErrorCodes } from "../../define/define";
	import { postApi, putApi, delApi } from "../../service/api";
	import {
		getHRLevelText,
		getHRLevelBgColor,
		getHRLevelBorderColor,
		getHRLevelBadgeColor,
		getImageUrl,
		handleImageError,
		ACTIVE_OPTIONS,
		HR_LEVEL_OPTIONS,
		SORT_OPTIONS,
	} from "../../utils/musicCommon";
	import { playAudio, stopAudio } from "../../utils/audio";
	import { downloadFile, downloadExcel } from "../../utils/download";

	// 상수 정의
	const BASE_URL = "/sound/musics";
	const SINGLE_URL = "/sound/music";

	// 타입 옵션
	const TYPE_OPTIONS = [
		{ value: "sleep", text: "수면" },
		{ value: "meditation", text: "명상" },
		{ value: "focus", text: "포커스" },
	];

	// 상태 관리
	let filteredData = [];
	let currentSort = "updated";
	let currentSortDirection = "asc";
	let searchKeyword = "";
	let currentType = "sleep";

	// 편집 아이템 초기값
	const defaultEditItem = {
		id: 0,
		idSoundSource: null,
		idCategory: null,
		subjectKo: "",
		subjectEn: "",
		subjectJa: "",
		subjectZhCn: "",
		subjectZhTw: "",
		isActive: false,
	};

	let editItem = { ...defaultEditItem };

	// 모달 상태 관리
	let showModal = false;
	let modalTitle = "";
	// 더 이상 사용하지 않는 상세보기 모달 제거됨

	// 컨텐츠장르 및 사운드 소스 데이터
	let categories = [];
	let sourceCategories = []; // 소스장르용 별도 배열
	let soundSources = [];
	let activeCategory = null;

	// 상태 추가
	let audioPlayer = null;
	let isPlaying = false;
	let isSaving = false;
	let currentPlayingId = null;
	let selectedItem = null; // 선택된 단일 아이템 정보

	// 사운드 소스 라이브러리 상태
	let showSourceLibrary = false;
	let sourceSearchKeyword = "";
	let sourceActiveCategory = null;
	let filteredSources = [];
	let sourcePlayingId = null;
	let sourceAudioPlayer = null;
	let sourceIsPlaying = false;
	let selectedSource = null; // 선택된 사운드 소스 정보

	// 컨텐츠장르 및 사운드 소스 정보 가져오기
	const loadCategories = async () => {
		await categoryAll.fetch("/contents/category");
		categories = $categoryAll.data || [];
	};

	// 소스장르 정보 가져오기
	const loadSourceCategories = async () => {
		await categoryAll.fetch("/contents/category/source");
		sourceCategories = $categoryAll.data || [];
	};

	const loadSoundSources = async () => {
		await soundSourcesAll.fetch("/sound/sources/1000/1/ALL");
		soundSources = $soundSourcesAll.data?.list || [];
		filteredSources = soundSources.filter((source) => source.isActive);
	};

	// 입력 핸들러
	const handleInput = (field, value) => {
		if (editItem[field] !== value) {
			editItem[field] = value;
		}
	};

	// 목록에서 음원 재생/정지 핸들러
	const handleListAudioPlay = (item) => {
		if (currentPlayingId === item.id && isPlaying) {
			stopAudio(
				audioPlayer,
				(v) => (audioPlayer = v),
				(v) => (isPlaying = v)
			);
			currentPlayingId = null;
		} else {
			if (isPlaying) {
				stopAudio(
					audioPlayer,
					(v) => (audioPlayer = v),
					(v) => (isPlaying = v)
				);
			}
			playAudio(
				item.fileName,
				{},
				(v) => (audioPlayer = v),
				(v) => (isPlaying = v),
				getResourceUrl
			);
			currentPlayingId = item.id;
		}
	};

	// 상세보기 관련 함수들 제거됨 - 이제 편집 모달이 상세 기능을 담당

	// 편집 함수
	const fnProcessEdit = (item) => {
		editItem = { ...item };
		// 기존 사운드 소스 정보 설정
		if (item.idSoundSource) {
			const source = soundSources.find((s) => s.id === item.idSoundSource);
			if (source) {
				selectedSource = { ...source };
			}
		}
		modalTitle = '<i class="bx bx-info-circle me-1"></i>컨텐츠음원 상세 정보';
		showModal = true;
		setTimeout(() => setFocus("subjectKo"), 100);
	};

	// 삭제 함수
	const fnDelete = async (id) => {
		const item = $all.data.list.find((item) => item.id === id);
		if (item.isActive) {
			alert("노출 중인 컨텐츠음원은 삭제할 수 없습니다.");
			return;
		}

		if (confirm("정말 삭제하시겠습니까?")) {
			await delApi(`${SINGLE_URL}/${id}`);
			await fnSearch();
		}
	};

	// 신규 등록 함수
	const fnProcessRegist = () => {
		let categoryId;

		// 현재 선택된 컨텐츠장르가 있으면 그것을 사용
		if (activeCategory !== null && activeCategory !== undefined) {
			categoryId = activeCategory;
		} else {
			// 선택된 컨텐츠장르가 없으면 현재 타입에 맞는 첫 번째 컨텐츠장르 찾기
			const typeNum = currentType === "sleep" ? 1 : currentType === "meditation" ? 2 : 3;
			const typeCategory = categories.find((cat) => cat.type === typeNum);
			categoryId = typeCategory ? typeCategory.id : categories.length > 0 ? categories[0].id : 0;
		}

		// 활성화된 사운드 소스 찾기
		const activeSources = soundSources.filter((source) => source.isActive);
		const soundSourceId = activeSources.length > 0 ? activeSources[0].id : 0;

		editItem = {
			...defaultEditItem,
			idCategory: categoryId,
			idSoundSource: soundSourceId,
		};
		modalTitle = '<i class="bx bx-plus-circle me-1"></i>컨텐츠음원 신규 등록';
		showModal = true;
		setTimeout(() => setFocus("subjectKo"), 100);
	};

	// 서버 적용 함수
	const fnRegistServer = async () => {
		if (confirm("서버에 적용하시겠습니까?\n컨텐츠음원 캐시 갱신 작업이 진행됩니다.")) {
			const result = await putApi("/sound/music/cache", {});
			if (result.resultCode === ErrorCodes.OK) {
				alert("서버 적용이 완료되었습니다.");
			} else {
				alert(result.resultDescKo);
			}
		}
	};

	// 음원 다운로드 함수
	const handleMusicDownload = () => {
		if (!editItem.fileName) {
			alert("다운로드할 음원 파일이 없습니다.");
			return;
		}

		const fileName = editItem.fileName;
		const subjectKo = editItem.subjectKo || "컨텐츠음원";
		const fileExtension = fileName.split(".").pop();
		const customFileName = `${subjectKo}.${fileExtension}`;

		// 커스텀 다운로드 함수
		const fullUrl = getResourceUrl(fileName);
		fetch(fullUrl)
			.then((response) => response.blob())
			.then((blob) => {
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = customFileName;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url);
				document.body.removeChild(a);
			})
			.catch((error) => {
				console.error("다운로드 실패:", error);
				alert("파일 다운로드에 실패했습니다.");
			});
	};

	// 소스음원 다운로드 함수
	const handleSourceDownload = (source) => {
		if (!source || !source.fileName) {
			alert("다운로드할 소스음원 파일이 없습니다.");
			return;
		}

		const fileName = source.fileName;
		const subject = source.subject || "소스음원";
		const fileExtension = fileName.split(".").pop();
		const customFileName = `${subject}.${fileExtension}`;

		// 소스음원 다운로드
		const fullUrl = getResourceUrl(fileName);
		fetch(fullUrl)
			.then((response) => response.blob())
			.then((blob) => {
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = customFileName;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url);
				document.body.removeChild(a);
			})
			.catch((error) => {
				console.error("소스음원 다운로드 실패:", error);
				alert("소스음원 파일 다운로드에 실패했습니다.");
			});
	};

	// 아이템 선택 함수
	const selectItem = (item) => {
		selectedItem = { ...item };
		// 사운드 소스 정보 추가
		const soundSource = soundSources.find((source) => source.id === item.idSoundSource);
		if (soundSource) {
			selectedItem.soundSource = soundSource;
		}
	};

	// 선택 해제 함수
	const clearSelection = () => {
		selectedItem = null;
		stopAudio(
			audioPlayer,
			(v) => (audioPlayer = v),
			(v) => (isPlaying = v)
		);
		currentPlayingId = null;
	};

	// 메서드
	const setFocus = (field = "subjectKo") => {
		const element = document.getElementById(field);
		if (element) {
			element.focus();
		}
	};

	const validateItem = () => {
		if (!editItem.subjectKo) {
			alert("한국어 제목을 입력하세요.");
			setFocus("subjectKo");
			return false;
		}
		if (!editItem.idSoundSource) {
			alert("사운드 소스를 선택하세요.");
			return false;
		}
		return true;
	};

	// 저장 함수
	const handleSave = async () => {
		if (!validateItem()) return;

		isSaving = true;
		try {
			const data = {
				id: editItem.id || 0,
				type: getTypeFromCategory(editItem.idCategory),
				idSoundSource: editItem.idSoundSource,
				idCategory: editItem.idCategory,
				subjectKo: editItem.subjectKo,
				subjectEn: editItem.subjectEn || "",
				subjectJa: editItem.subjectJa || "",
				subjectZhCn: editItem.subjectZhCn || "",
				subjectZhTw: editItem.subjectZhTw || "",
				isActive: editItem.isActive,
			};

			let result;
			if (editItem.id) {
				result = await putApi(SINGLE_URL, data);
			} else {
				result = await postApi(SINGLE_URL, data);
			}

			if (result.resultCode === ErrorCodes.OK) {
				alert(editItem.id ? "수정되었습니다." : "등록되었습니다.");
				closeModal();
				await fnSearch();
			} else {
				alert(result.resultDescKo || "저장 중 오류가 발생했습니다.");
			}
		} catch (error) {
			console.error("Save error:", error);
			alert("저장 중 오류가 발생했습니다.");
		} finally {
			isSaving = false;
		}
	};

	// 컨텐츠장르에서 타입 추출
	const getTypeFromCategory = (categoryId) => {
		const category = categories.find((cat) => cat.id === categoryId);
		if (!category) return "sleep";

		switch (category.type) {
			case 1:
				return "sleep";
			case 2:
				return "meditation";
			case 3:
				return "focus";
			default:
				return "sleep";
		}
	};

	// 모달 닫기
	const closeModal = () => {
		showModal = false;
		editItem = { ...defaultEditItem };
		selectedSource = null; // 선택된 소스 정보 초기화
		stopAudio(
			audioPlayer,
			(v) => (audioPlayer = v),
			(v) => (isPlaying = v)
		);
		currentPlayingId = null;
	};

	const fnSearch = async () => {
		const url = `${BASE_URL}/1000/1/${currentType}/${searchKeyword ? encodeURIComponent(searchKeyword) : "ALL"}`;
		await all.fetch(url);
		if ($all?.data) {
			filteredData = getFilteredData($all.data);
		}
	};

	const fnReload = () => {
		searchKeyword = "";
		currentSort = "updated";
		currentSortDirection = "asc";
		currentType = "sleep";
		fnSearch();
	};

	// Data processing functions
	const getFilteredData = (data) => {
		if (!data || !data.list) return [];

		let list = data.list;

		if (searchKeyword && searchKeyword.trim() !== "") {
			const keyword = searchKeyword.toLowerCase();
			list = list.filter((item) => {
				return (
					item.subjectKo?.toLowerCase().includes(keyword) ||
					item.subjectEn?.toLowerCase().includes(keyword) ||
					item.sourceSubject?.toLowerCase().includes(keyword)
				);
			});
		}

		if (activeCategory !== null && activeCategory !== undefined) {
			list = list.filter((item) => item.idCategory === activeCategory);
		}

		return sortData(list);
	};

	// 정렬 함수
	const sortData = (list) => {
		const sortedList = [...list];
		const direction = currentSortDirection === "asc" ? 1 : -1;

		switch (currentSort) {
			case "updated":
				sortedList.sort((a, b) => direction * (new Date(b.updatedAt) - new Date(a.updatedAt)));
				break;
			case "created":
				sortedList.sort((a, b) => direction * (new Date(b.createdAt) - new Date(a.createdAt)));
				break;
			case "title":
				sortedList.sort((a, b) => direction * a.subjectKo.localeCompare(b.subjectKo));
				break;
			case "genre":
				sortedList.sort((a, b) => {
					const catA = getCategoryName(a.idCategory);
					const catB = getCategoryName(b.idCategory);
					return direction * catA.localeCompare(catB);
				});
				break;
			case "active":
				sortedList.sort((a, b) => {
					const activeA = a.isActive ? 1 : 0;
					const activeB = b.isActive ? 1 : 0;
					return direction * (activeB - activeA);
				});
				break;
			case "level":
				sortedList.sort((a, b) => direction * (b.level - a.level));
				break;
			case "category":
				sortedList.sort((a, b) => {
					const catA = getCategoryName(a.idCategory);
					const catB = getCategoryName(b.idCategory);
					return direction * catA.localeCompare(catB);
				});
				break;
		}

		return sortedList;
	};

	// 반응성 선언
	$: if ($all?.data) {
		filteredData = getFilteredData($all.data);
	}

	$: if (currentSort || currentSortDirection) {
		if ($all?.data) {
			filteredData = getFilteredData($all.data);
		}
	}

	$: if (activeCategory !== undefined) {
		if ($all?.data) {
			filteredData = getFilteredData($all.data);
		}
	}

	// 컨텐츠장르 이름 가져오기
	const getCategoryName = (idCategory) => {
		const category = categories.find((cat) => cat.id === idCategory);
		return category ? category.subjectKo : "컨텐츠장르 미지정";
	};

	const setActiveCategory = (categoryId) => {
		activeCategory = categoryId;
	};

	// 타입 변경 핸들러
	const handleTypeChange = (type) => {
		currentType = type;
		activeCategory = null; // 타입 변경 시 컨텐츠장르 필터 초기화
		fnSearch();
	};

	// 사운드 소스 라이브러리 관련 함수들
	const openSourceLibrary = () => {
		sourceSearchKeyword = "";
		sourceActiveCategory = null;
		filterSources();
		showSourceLibrary = true;
	};

	const closeSourceLibrary = () => {
		showSourceLibrary = false;
		if (sourceAudioPlayer) {
			stopAudio(
				sourceAudioPlayer,
				(v) => (sourceAudioPlayer = v),
				(v) => (sourceIsPlaying = v)
			);
			sourcePlayingId = null;
		}
	};

	const selectSource = (source) => {
		editItem.idSoundSource = source.id;
		selectedSource = { ...source }; // 선택된 소스 정보 저장
		closeSourceLibrary(); // 라이브러리 모달 닫기
	};

	const filterSources = () => {
		let sources = soundSources.filter((source) => source.isActive);

		// 소스장르 필터링
		if (sourceActiveCategory !== null) {
			sources = sources.filter((source) => source.idCategorySource === sourceActiveCategory);
		}

		// 검색 필터링
		if (sourceSearchKeyword && sourceSearchKeyword.trim() !== "") {
			const keyword = sourceSearchKeyword.toLowerCase();
			sources = sources.filter((source) => source.subject?.toLowerCase().includes(keyword));
		}

		filteredSources = sources;
	};

	const handleSourceAudioPlay = (source) => {
		if (sourcePlayingId === source.id && sourceIsPlaying) {
			stopAudio(
				sourceAudioPlayer,
				(v) => (sourceAudioPlayer = v),
				(v) => (sourceIsPlaying = v)
			);
			sourcePlayingId = null;
		} else {
			if (sourceIsPlaying) {
				stopAudio(
					sourceAudioPlayer,
					(v) => (sourceAudioPlayer = v),
					(v) => (sourceIsPlaying = v)
				);
			}
			playAudio(
				source.fileName,
				{},
				(v) => (sourceAudioPlayer = v),
				(v) => (sourceIsPlaying = v),
				getResourceUrl
			);
			sourcePlayingId = source.id;
		}
	};

	// 반응성 선언 - 소스 필터링
	$: if (sourceSearchKeyword !== undefined || sourceActiveCategory !== undefined) {
		filterSources();
	}

	// 엑셀 다운로드 함수
	const handleExcelDownload = async () => {
		try {
			// 전체 데이터 가져오기 (검색 필터 제거)
			const promises = [];

			// 각 타입별로 데이터 가져오기
			for (const typeOption of TYPE_OPTIONS) {
				const url = `${BASE_URL}/10000/1/${typeOption.value}/ALL`; // 충분히 큰 페이지 사이즈로 전체 데이터 조회
				promises.push(all.fetch(url).then(() => ({ type: typeOption.value, data: $all.data })));
			}

			const allTypeData = await Promise.all(promises);

			// 전체 데이터를 합치기
			const allData = [];
			allTypeData.forEach(({ data }) => {
				if (data?.list) {
					allData.push(...data.list);
				}
			});

			if (allData.length === 0) {
				alert("다운로드할 데이터가 없습니다.");
				return;
			}

			// 데이터를 타입별 및 컨텐츠장르별로 분류
			const dataByTypeAndCategory = {};

			// 전체 탭 데이터
			const allExcelData = allData.map((item) => ({
				ID: item.id,
				타입: TYPE_OPTIONS.find((t) => t.value === getTypeFromCategory(item.idCategory))?.text || "기타",
				컨텐츠장르: getCategoryName(item.idCategory),
				"제목(한국어)": item.subjectKo || "",
				"제목(영어)": item.subjectEn || "",
				"제목(일본어)": item.subjectJa || "",
				"제목(중국어-간체)": item.subjectZhCn || "",
				"제목(중국어-번체)": item.subjectZhTw || "",
				사운드소스: item.sourceSubject || "",
				"HR Level": getHRLevelText(item.level),
				음원파일명: item.fileName || "",
				이미지파일명: item.musicImage || "",
				노출여부: item.isActive ? "노출" : "미노출",
				생성일시: item.createdAt ? Dates.defaultConvertFull(item.createdAt) : "",
				수정일시: item.updatedAt ? Dates.defaultConvertFull(item.updatedAt) : "",
			}));

			dataByTypeAndCategory["전체"] = allExcelData;

			// 타입별로 데이터 분류
			TYPE_OPTIONS.forEach((typeOption) => {
				const typeData = allData
					.filter((item) => {
						const itemType = getTypeFromCategory(item.idCategory);
						return itemType === typeOption.value;
					})
					.map((item) => ({
						ID: item.id,
						컨텐츠장르: getCategoryName(item.idCategory),
						"제목(한국어)": item.subjectKo || "",
						"제목(영어)": item.subjectEn || "",
						"제목(일본어)": item.subjectJa || "",
						"제목(중국어-간체)": item.subjectZhCn || "",
						"제목(중국어-번체)": item.subjectZhTw || "",
						사운드소스: item.sourceSubject || "",
						"HR Level": getHRLevelText(item.level),
						음원파일명: item.fileName || "",
						이미지파일명: item.musicImage || "",
						노출여부: item.isActive ? "노출" : "미노출",
						생성일시: item.createdAt ? Dates.defaultConvertFull(item.createdAt) : "",
						수정일시: item.updatedAt ? Dates.defaultConvertFull(item.updatedAt) : "",
					}));

				if (typeData.length > 0) {
					dataByTypeAndCategory[typeOption.text] = typeData;
				}
			});

			// 컨텐츠장르별로 데이터 분류 (현재 선택된 타입에서만)
			const currentTypeCategories = categories.filter((cat) => {
				const typeNum = currentType === "sleep" ? 1 : currentType === "meditation" ? 2 : 3;
				return cat.type === typeNum;
			});

			currentTypeCategories.forEach((category) => {
				const categoryData = allData
					.filter((item) => item.idCategory === category.id)
					.map((item) => ({
						ID: item.id,
						"제목(한국어)": item.subjectKo || "",
						"제목(영어)": item.subjectEn || "",
						"제목(일본어)": item.subjectJa || "",
						"제목(중국어-간체)": item.subjectZhCn || "",
						"제목(중국어-번체)": item.subjectZhTw || "",
						사운드소스: item.sourceSubject || "",
						"HR Level": getHRLevelText(item.level),
						음원파일명: item.fileName || "",
						이미지파일명: item.musicImage || "",
						노출여부: item.isActive ? "노출" : "미노출",
						생성일시: item.createdAt ? Dates.defaultConvertFull(item.createdAt) : "",
						수정일시: item.updatedAt ? Dates.defaultConvertFull(item.updatedAt) : "",
					}));

				if (categoryData.length > 0) {
					dataByTypeAndCategory[`${TYPE_OPTIONS.find((t) => t.value === currentType)?.text || currentType}_${category.subjectKo}`] =
						categoryData;
				}
			});

			// 미지정 컨텐츠장르 데이터
			const unassignedData = allData
				.filter((item) => !categories.find((cat) => cat.id === item.idCategory))
				.map((item) => ({
					ID: item.id,
					타입: TYPE_OPTIONS.find((t) => t.value === getTypeFromCategory(item.idCategory))?.text || "기타",
					컨텐츠장르: "컨텐츠장르 미지정",
					"제목(한국어)": item.subjectKo || "",
					"제목(영어)": item.subjectEn || "",
					"제목(일본어)": item.subjectJa || "",
					"제목(중국어-간체)": item.subjectZhCn || "",
					"제목(중국어-번체)": item.subjectZhTw || "",
					사운드소스: item.sourceSubject || "",
					"HR Level": getHRLevelText(item.level),
					음원파일명: item.fileName || "",
					이미지파일명: item.musicImage || "",
					노출여부: item.isActive ? "노출" : "미노출",
					생성일시: item.createdAt ? Dates.defaultConvertFull(item.createdAt) : "",
					수정일시: item.updatedAt ? Dates.defaultConvertFull(item.updatedAt) : "",
				}));

			if (unassignedData.length > 0) {
				dataByTypeAndCategory["컨텐츠장르 미지정"] = unassignedData;
			}

			// 현재 날짜와 시간으로 파일명 생성
			const now = new Date();
			const dateStr = now.toISOString().split("T")[0].replace(/-/g, "");
			const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "");
			const fileName = `컨텐츠음원_${dateStr}_${timeStr}.xlsx`;

			await downloadExcel(dataByTypeAndCategory, fileName);
		} catch (error) {
			console.error("Excel download error:", error);
			alert("엑셀 다운로드 중 오류가 발생했습니다.");
		}
	};

	onMount(async () => {
		menu.set("컨텐츠 관리");
		menuSub.set("음원 관리");
		menuSubSub.set("컨텐츠음원 관리");

		await loadCategories();
		await loadSourceCategories();
		await loadSoundSources();
		await fnSearch();
	});
</script>

<div class="card mb-4">
	<div class="p-3">
		<!-- 타입 탭 -->
		<ul class="nav nav-tabs d-flex mb-2" role="tablist">
			{#each TYPE_OPTIONS as typeOption}
				<li class="nav-item">
					<button
						class="nav-link {currentType === typeOption.value ? 'active border-top border-start border-end border-secondary' : ''}"
						style="cursor: pointer;"
						on:click={() => handleTypeChange(typeOption.value)}
					>
						{typeOption.text}
					</button>
				</li>
			{/each}
		</ul>

		<!-- 컨텐츠장르 탭 -->
		<ul class="nav nav-tabs nav-tabs-sm mb-2 d-flex align-items-center" role="tablist" style="min-height: 36px;">
			<li class="nav-item">
				<a
					class="nav-link {activeCategory === null ? 'active' : ''}"
					on:click|preventDefault={() => setActiveCategory(null)}
					href="#category-all"
					role="tab"
					style="height: 28px; display: flex; align-items: center; font-size: 0.75rem; padding: 0.2rem 0.4rem;"
				>
					전체
				</a>
			</li>
			{#each categories.filter((cat) => cat.type === (currentType === "sleep" ? 1 : currentType === "meditation" ? 2 : currentType === "focus" ? 3 : cat.type)) as category}
				<li class="nav-item">
					<a
						class="nav-link {activeCategory === category.id ? 'active' : ''}"
						on:click|preventDefault={() => setActiveCategory(category.id)}
						href="#category-{category.id}"
						role="tab"
						style="height: 28px; display: flex; align-items: center; font-size: 0.75rem; padding: 0.2rem 0.4rem;"
					>
						{category.subjectKo}
					</a>
				</li>
			{/each}

			<!-- 검색 및 컨트롤 -->
			<div class="ms-auto d-flex align-items-center flex-grow-1" style="padding: 0 5px;">
				<div class="d-flex align-items-center flex-grow-1 me-2 justify-content-end">
					<input
						type="text"
						class="form-control form-control-sm me-2"
						placeholder="제목 또는 소스명 검색"
						style="width: 200px; height: 28px; font-size: 0.8rem;"
						bind:value={searchKeyword}
						on:keydown={(e) => {
							if (e.key === "Enter") {
								fnSearch();
							}
						}}
					/>
					<select class="form-select form-select-sm" style="width: auto; height: 28px; font-size: 0.8rem;" bind:value={currentSort}>
						{#each SORT_OPTIONS as option}
							<option value={option.value}>{option.text}</option>
						{/each}
					</select>
				</div>
				<div class="btn-group me-2">
					<button
						type="button"
						class="btn btn-sm {currentSortDirection === 'asc' ? 'btn-primary' : 'btn-outline-primary'}"
						title="오름차순"
						style="height: 28px; display: flex; align-items: center; padding: 0.25rem 0.5rem;"
						on:click={() => (currentSortDirection = "asc")}
					>
						<i class="bx bx-sort-up" style="font-size: 0.9rem;"></i>
					</button>
					<button
						type="button"
						class="btn btn-sm {currentSortDirection === 'desc' ? 'btn-primary' : 'btn-outline-primary'}"
						title="내림차순"
						style="height: 28px; display: flex; align-items: center; padding: 0.25rem 0.5rem;"
						on:click={() => (currentSortDirection = "desc")}
					>
						<i class="bx bx-sort-down" style="font-size: 0.9rem;"></i>
					</button>
				</div>
				<TableToolbarNoPaging onReload={fnReload} onRegist={fnProcessRegist} onRegistServer={fnRegistServer} />
				<button type="button" class="btn btn-sm btn-warning me-1" on:click={handleExcelDownload} title="엑셀 다운로드">
					<i class="bx bx-download me-1"></i>
					엑셀
				</button>
			</div>
		</ul>

		<!-- 선택된 아이템 정보 패널 -->
		{#if selectedItem}
			<div class="selected-item-panel mb-4">
				<div class="d-flex justify-content-between align-items-center mb-3">
					<h5 class="mb-0">
						<i class="bx bx-info-circle text-primary me-2"></i>
						선택된 컨텐츠음원 정보
					</h5>
					<button class="btn btn-sm btn-outline-secondary" on:click={clearSelection}>
						<i class="bx bx-x"></i>
						닫기
					</button>
				</div>

				<div class="row">
					<!-- 이미지 및 기본 정보 -->
					<div class="col-md-4">
						<div class="text-center mb-3">
							{#if selectedItem.musicImage}
								<img
									src={selectedItem.musicImage}
									alt={selectedItem.subjectKo}
									class="img-fluid rounded"
									style="max-height: 200px; width: auto;"
								/>
							{:else}
								<div class="bg-light rounded d-flex align-items-center justify-content-center" style="height: 200px;">
									<i class="bx bx-image fs-1 text-muted"></i>
								</div>
							{/if}
						</div>

						<!-- 재생 컨트롤 -->
						{#if selectedItem.fileName}
							<div class="text-center mb-3">
								<button
									class="btn {currentPlayingId === selectedItem.id && isPlaying ? 'btn-danger' : 'btn-primary'} btn-lg"
									on:click={() => handleListAudioPlay(selectedItem)}
									title={currentPlayingId === selectedItem.id && isPlaying ? "정지" : "재생"}
								>
									<i class="bx {currentPlayingId === selectedItem.id && isPlaying ? 'bx-stop' : 'bx-play'} me-2"></i>
									{currentPlayingId === selectedItem.id && isPlaying ? "정지" : "재생"}
								</button>
							</div>
						{/if}
					</div>

					<!-- 상세 정보 -->
					<div class="col-md-8">
						<div class="row g-3">
							<div class="col-md-6">
								<div class="fw-bold text-secondary mb-1">한국어 제목</div>
								<div class="form-control-plaintext border rounded p-2 bg-light">
									{selectedItem.subjectKo || "-"}
								</div>
							</div>
							<div class="col-md-6">
								<div class="fw-bold text-secondary mb-1">영어 제목</div>
								<div class="form-control-plaintext border rounded p-2 bg-light">
									{selectedItem.subjectEn || "-"}
								</div>
							</div>
							<div class="col-md-6">
								<div class="fw-bold text-secondary mb-1">일본어 제목</div>
								<div class="form-control-plaintext border rounded p-2 bg-light">
									{selectedItem.subjectJa || "-"}
								</div>
							</div>
							<div class="col-md-6">
								<div class="fw-bold text-secondary mb-1">중국어(간체) 제목</div>
								<div class="form-control-plaintext border rounded p-2 bg-light">
									{selectedItem.subjectZhCn || "-"}
								</div>
							</div>
							<div class="col-md-6">
								<div class="fw-bold text-secondary mb-1">중국어(번체) 제목</div>
								<div class="form-control-plaintext border rounded p-2 bg-light">
									{selectedItem.subjectZhTw || "-"}
								</div>
							</div>
							<div class="col-md-6">
								<div class="fw-bold text-secondary mb-1">컨텐츠장르</div>
								<div class="form-control-plaintext border rounded p-2 bg-light">
									{selectedItem.categoryNameKo || "-"}
								</div>
							</div>
							<div class="col-md-6">
								<div class="fw-bold text-secondary mb-1">심박수 레벨</div>
								<div class="form-control-plaintext border rounded p-2 bg-light">
									<span class="badge {getHRLevelBadgeColor(selectedItem.level)}">
										{getHRLevelText(selectedItem.level)}
									</span>
								</div>
							</div>
							<div class="col-md-6">
								<div class="fw-bold text-secondary mb-1">노출 상태</div>
								<div class="form-control-plaintext border rounded p-2 bg-light">
									<span class="badge {selectedItem.isActive ? 'bg-success' : 'bg-secondary'}">
										{selectedItem.isActive ? "노출" : "미노출"}
									</span>
								</div>
							</div>
							<div class="col-md-6">
								<div class="fw-bold text-secondary mb-1">파일명</div>
								<div class="form-control-plaintext border rounded p-2 bg-light">
									<span
										class="text-decoration-underline"
										style="cursor: pointer; color: #0d6efd;"
										on:click={() => handleSourceDownload(selectedItem.soundSource)}
										title="클릭하여 다운로드"
									>
										{selectedItem.soundSource.fileName || "-"}
									</span>
								</div>
							</div>
						</div>

						<!-- 사운드 소스 정보 -->
						{#if selectedItem.soundSource}
							<hr class="my-3" />
							<h6 class="fw-bold mb-3">
								<i class="bx bx-music text-primary me-2"></i>
								사운드 소스 정보
							</h6>
							<div class="row g-3">
								<div class="col-md-6">
									<div class="fw-bold text-secondary mb-1">소스 제목</div>
									<div class="form-control-plaintext border rounded p-2 bg-light">
										{selectedItem.soundSource.subject || "-"}
									</div>
								</div>
								<div class="col-md-6">
									<div class="fw-bold text-secondary mb-1">파일명</div>
									<div class="form-control-plaintext border rounded p-2 bg-light">
										<span
											class="text-decoration-underline"
											style="cursor: pointer; color: #0d6efd;"
											on:click={() => handleSourceDownload(selectedItem.soundSource)}
											title="클릭하여 다운로드"
										>
											{selectedItem.soundSource.fileName || "-"}
										</span>
									</div>
								</div>
								<div class="col-md-12">
									<div class="fw-bold text-secondary mb-1">설명</div>
									<div class="form-control-plaintext border rounded p-2 bg-light" style="min-height: 60px;">
										{selectedItem.soundSource.description || "-"}
									</div>
								</div>
							</div>

							<!-- 소스장르 정보 -->
							<div class="row g-3 mt-2">
								<div class="col-md-12">
									<div class="fw-bold text-secondary mb-1">소스장르</div>
									<div class="form-control-plaintext border rounded p-2 bg-light">
										<span class="badge bg-light text-dark border">
											{sourceCategories.find((c) => c.id === selectedItem.soundSource?.idCategorySource)?.subject ||
												"소스장르 미지정"}
										</span>
									</div>
								</div>
							</div>
						{/if}

						<!-- 액션 버튼들 -->
						<hr class="my-3" />
						<div class="d-flex gap-2">
							<button class="btn btn-primary" on:click={() => fnProcessEdit(selectedItem)}>
								<i class="bx bx-info-circle me-1"></i>
								상세
							</button>
							<button class="btn btn-outline-danger" on:click={() => fnDelete(selectedItem.id)}>
								<i class="bx bx-trash me-1"></i>
								삭제
							</button>
						</div>
					</div>
				</div>
			</div>
		{/if}

		<!-- 카드 뷰 컨테이너 -->
		{#if filteredData.length === 0}
			<div class="text-center p-5">
				<i class="bx bx-info-circle fs-1 text-muted"></i>
				<p class="text-muted mt-3">데이터가 없습니다.</p>
			</div>
		{:else}
			<div class="row g-3" style="display: flex; flex-wrap: wrap;">
				{#each filteredData as item, index (`${item.id}_${index}`)}
					<div style="width: 230px; margin-bottom: 1rem; padding: 0 0.5rem;" in:fade={{ duration: 300, delay: index * 20 }}>
						<div class="card h-100 border border-opacity-50 {selectedItem && selectedItem.id === item.id ? 'selected-card' : ''}">
							<div class="position-relative">
								{#if item.musicImage}
									<div style="height: 135px; overflow: hidden;">
										<img
											src={item.musicImage}
											alt={item.subjectKo}
											class="w-100 h-100 card-img-top"
											style="object-fit: cover; height: 135px;"
											on:error={handleImageError}
										/>
									</div>
								{:else}
									<div style="height: 135px; overflow: hidden;">
										<img
											src="/assets/img/nope-not-here.avif"
											alt="이미지 없음"
											style="width: 230px; height: 135px; object-fit: cover;"
										/>
									</div>
								{/if}
								<div class="position-absolute top-0 start-0 p-2 d-flex justify-content-between w-100">
									<span class="badge {item.isActive ? 'bg-success text-dark' : 'bg-secondary text-dark'} fw-bold">
										{item.isActive ? "노출" : "미노출"}
									</span>
									<span class="badge {getHRLevelBadgeColor(item.level)}">
										{getHRLevelText(item.level)}
									</span>
								</div>
							</div>
							<div class="card-body p-2">
								<h6 class="card-title mb-1" style="cursor: pointer; color: #0d6efd;" on:click={() => selectItem(item)}>
									{item.subjectKo}
								</h6>
								<div class="mt-2">
									<p class="card-text mb-0 small">
										컨텐츠장르: {item.categoryNameKo}
									</p>
									<p class="card-text mb-0 small">
										소스: {item.sourceSubject}
									</p>
								</div>
							</div>
							<div class="card-footer d-flex justify-content-center align-items-center p-2 bg-light">
								<div class="btn-group">
									{#if item.fileName}
										<button
											class="btn btn-sm btn-outline-primary"
											on:click={() => handleListAudioPlay(item)}
											title={currentPlayingId === item.id && isPlaying ? "정지" : "재생"}
										>
											<i class="bx {currentPlayingId === item.id && isPlaying ? 'bx-stop' : 'bx-play'}"></i>
										</button>
									{/if}
									<button class="btn btn-primary btn-sm" on:click={() => fnProcessEdit(item)}>상세</button>
									<button class="btn btn-sm btn-outline-primary" on:click={() => fnDelete(item.id)}>삭제</button>
								</div>
							</div>
						</div>
					</div>
				{/each}
			</div>
		{/if}
	</div>

	<style>
		/* 선택된 아이템 패널 스타일 */
		.selected-item-panel {
			background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
			border: 2px solid #0d6efd;
			border-radius: 0.5rem;
			padding: 1.5rem;
			box-shadow: 0 4px 16px rgba(13, 110, 253, 0.1);
			animation: slideIn 0.3s ease-out;
		}

		@keyframes slideIn {
			from {
				opacity: 0;
				transform: translateY(-20px);
			}
			to {
				opacity: 1;
				transform: translateY(0);
			}
		}

		.selected-item-panel .form-control-plaintext {
			background-color: #ffffff !important;
			border: 1px solid #dee2e6;
			font-size: 0.9rem;
			min-height: 38px;
			display: flex;
			align-items: center;
		}

		.selected-item-panel .form-label {
			color: #495057;
			font-size: 0.875rem;
			margin-bottom: 0.25rem;
		}

		/* 선택된 카드 스타일 */
		.selected-card {
			border-color: #0d6efd !important;
			border-width: 2px !important;
			box-shadow: 0 4px 12px rgba(13, 110, 253, 0.2) !important;
			transform: scale(1.02);
			transition: all 0.2s ease;
		}

		/* 선택된 사운드 소스 패널 스타일 */
		.selected-source-panel {
			background: linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%);
			border: 1px solid #e3f2fd;
			box-shadow: 0 2px 12px rgba(13, 110, 253, 0.08);
			transition: all 0.3s ease;
		}

		.source-info-content {
			padding: 0.5rem 0;
		}

		.source-info-content h6 {
			font-size: 1.1rem;
			line-height: 1.3;
			margin-bottom: 0.5rem;
		}

		.source-info-content .text-muted {
			font-size: 0.9rem;
			line-height: 1.4;
		}

		.table-container {
			padding: 0 15px;
			width: 100%;
		}

		.custom-table {
			border-collapse: collapse;
			border: 1px solid #ddd;
			margin-bottom: 15px;
			font-family: var(--bs-body-font-family);
			font-size: 13px;
			box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
			width: 100%;
		}

		/* 헤더 스타일 */
		.custom-table thead tr {
			text-align: center;
			background-color: #f2f2f2;
			border-bottom: 2px solid #ddd;
		}

		/* 셀 공통 스타일 */
		.custom-table th,
		.custom-table td {
			padding: 4px;
			border: 1px solid #ddd;
			text-align: center;
		}

		/* 짝수/홀수 행 스타일 */
		.even-model {
			background-color: #ffffff;
			padding: 4px;
			border: 1px solid #ddd;
			font-size: 13px;
		}

		.odd-model {
			background-color: #f9f9f9;
			padding: 4px;
			border: 1px solid #ddd;
			font-size: 13px;
		}

		/* 호버 효과 */
		.custom-table tr:hover {
			background-color: #f0f8ff !important;
		}

		/* 데이터 없음 메시지 */
		.no-data {
			text-align: center;
		}

		/* 배지 스타일 */
		.badge {
			font-size: 13px;
			padding: 5px 10px;
		}

		/* 전역 스타일 - 필요한 경우 */
		:global(table) {
			font-family: var(--bs-body-font-family) !important;
			font-size: 12px !important;
		}

		:global(table td),
		:global(table th) {
			font-size: 12px !important;
		}

		.model-link {
			cursor: pointer;
			color: #0d6efd;
			text-decoration: underline;
		}
		.model-link:hover {
			color: #084298;
		}

		button {
			padding: 8px 16px;
			background-color: #4caf50;
			color: white;
			border: none;
			border-radius: 4px;
			cursor: pointer;
		}

		button:hover {
			background-color: #45a049;
		}

		.fade-container {
			opacity: 1;
			transition: opacity 0.15s linear;
		}

		.nav-tabs-sm {
			border-bottom: 1px solid #b3acf2;
			min-height: 36px;
		}

		.nav-tabs .nav-link {
			padding: 0.5rem 1rem;
			font-size: 0.875rem;
			font-weight: 600;
			color: #495057;
		}

		.nav-tabs .nav-link.active {
			font-weight: 700;
			color: #212529;
		}

		.nav-tabs-sm .nav-link {
			border-bottom: 1px solid #b3acf2;
			margin-right: 0.125rem;
			border-top-left-radius: 0.2rem;
			border-top-right-radius: 0.2rem;
			padding: 0.2rem 0.4rem;
			font-size: 0.75rem;
			height: 28px;
			display: flex;
			align-items: center;
		}

		.nav-tabs-sm .nav-link.active {
			border-top: 1px solid #b3acf2;
			border-left: 1px solid #b3acf2;
			border-right: 1px solid #b3acf2;
			font-weight: bold;
		}

		/* 사운드 소스 카드 스타일 */
		.source-card {
			transition: all 0.2s ease;
			border: 2px solid #e9ecef;
			border-radius: 0.375rem;
			overflow: hidden;
			margin: 2px 0; /* 상하 여백 추가로 이동 공간 확보 */
		}

		.source-card:hover {
			transform: translateY(-2px);
			box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
			border-color: #0d6efd;
			border-width: 2px; /* 테두리 두께 명시적 지정 */
		}

		.source-card.selected {
			border-color: #198754;
			border-width: 2px; /* 테두리 두께 명시적 지정 */
			box-shadow: 0 4px 12px rgba(25, 135, 84, 0.3);
		}

		.source-card.selected:hover {
			transform: translateY(-2px);
			box-shadow: 0 6px 16px rgba(25, 135, 84, 0.4);
		}
	</style>
</div>

<!-- 편집/등록 모달 -->
{#if showModal}
	<div
		class="modal fade {showModal ? 'show' : ''}"
		style="display: block;"
		tabindex="-1"
		aria-modal="true"
		role="dialog"
		transition:fade={{ duration: 200 }}
	>
		<div class="modal-dialog modal-dialog-centered modal-lg">
			<div class="modal-content">
				<div class="modal-header {!editItem.id ? 'bg-light' : ''}">
					<h5 class="modal-title d-flex align-items-center">
						{@html modalTitle}
					</h5>
					<button type="button" class="btn-close" on:click={closeModal}></button>
				</div>
				<div class="modal-body">
					<!-- 기본 정보 -->
					<div class="mb-4">
						<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
							<i class="bx bx-info-circle me-2"></i>
							기본 정보
						</h6>
						<div class="row">
							<div class="col-md-6">
								<div class="mb-3">
									<label for="idCategory" class="form-label">컨텐츠장르</label>
									<select
										class="form-select form-select-sm"
										id="idCategory"
										value={editItem.idCategory}
										on:change={(e) => handleInput("idCategory", parseInt(e.target.value))}
									>
										{#each categories.filter((cat) => cat.type === (currentType === "sleep" ? 1 : currentType === "meditation" ? 2 : currentType === "focus" ? 3 : cat.type)) as category}
											<option value={category.id}>
												{category.subjectKo} ({category.id})
											</option>
										{/each}
									</select>
								</div>
							</div>
							<div class="col-md-6">
								<div class="mb-3">
									<label for="idSoundSource" class="form-label">사운드 소스</label>
									<div class="input-group">
										<input
											type="text"
											class="form-control form-control-sm"
											id="idSoundSource"
											value={soundSources.find((s) => s.id === editItem.idSoundSource)?.subject || ""}
											readonly
											placeholder="사운드 소스를 선택하세요"
										/>
										<button type="button" class="btn btn-outline-secondary btn-sm" on:click={openSourceLibrary}>
											<i class="bx bx-search"></i>
											선택
										</button>
									</div>
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col-12">
								<div class="mb-3">
									<div class="form-check form-switch">
										<input class="form-check-input" type="checkbox" id="isActive" bind:checked={editItem.isActive} />
										<label class="form-check-label" for="isActive">노출 여부</label>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- 선택된 사운드 소스 정보 패널 -->
					{#if selectedSource}
						<div class="mb-4">
							<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-primary text-white p-2 rounded">
								<i class="bx bx-music me-2"></i>
								선택된 사운드 소스 정보
							</h6>
							<div class="selected-source-panel p-3 border rounded bg-light">
								<div class="row">
									<div class="col-md-4 text-center">
										{#if selectedSource.musicImage}
											<img
												src={selectedSource.musicImage}
												alt={selectedSource.subject}
												class="img-fluid rounded"
												style="max-height: 120px; width: auto;"
											/>
										{:else}
											<div class="bg-secondary rounded d-flex align-items-center justify-content-center" style="height: 120px;">
												<i class="bx bx-music fs-1 text-white"></i>
											</div>
										{/if}
									</div>
									<div class="col-md-8">
										<div class="source-info-content">
											<div class="mb-3">
												<h6 class="fw-bold text-primary mb-1">
													<i class="bx bx-music me-1"></i>
													{selectedSource.subject || "제목 없음"}
												</h6>
												<div class="text-muted small mb-2">
													<i class="bx bx-file me-1"></i>
													<span
														class="text-decoration-underline"
														style="cursor: pointer; color: #0d6efd;"
														on:click={() => handleSourceDownload(selectedSource)}
														title="클릭하여 다운로드"
													>
														{selectedSource.fileName || "파일명 없음"}
													</span>
												</div>
											</div>
											{#if selectedSource.description}
												<div class="mb-3">
													<span class="text-secondary small fw-semibold">설명</span>
													<div class="mt-1 text-dark">
														{selectedSource.description}
													</div>
												</div>
											{/if}

											<!-- 레벨 정보 추가-->
											<div class="mb-3">
												<span class="text-secondary small fw-semibold">레벨</span>
												<span class="badge bg-light text-dark border">
													{getHRLevelText(selectedSource.level)}
												</span>
												&nbsp;&nbsp;&nbsp;
												<span class="text-secondary small fw-semibold">소스장르</span>
												<span class="badge bg-light text-dark border">
													{sourceCategories.find((c) => c.id === selectedSource.idCategorySource)?.subject ||
														"소스장르 미지정"}
												</span>
											</div>

											<div class="d-flex gap-2 align-items-center">
												{#if selectedSource.fileName}
													<button
														class="btn {sourcePlayingId === selectedSource.id && sourceIsPlaying
															? 'btn-danger'
															: 'btn-primary'} btn-sm"
														on:click={() => handleSourceAudioPlay(selectedSource)}
														title={sourcePlayingId === selectedSource.id && sourceIsPlaying ? "정지" : "재생"}
													>
														<i
															class="bx {sourcePlayingId === selectedSource.id && sourceIsPlaying
																? 'bx-stop'
																: 'bx-play'} me-1"
														></i>
														{sourcePlayingId === selectedSource.id && sourceIsPlaying ? "정지" : "재생"}
													</button>
												{/if}
												<button class="btn btn-outline-primary btn-sm" on:click={openSourceLibrary}>
													<i class="bx bx-refresh me-1"></i>
													다른 소스 선택
												</button>
												<span class="badge bg-success small">
													<i class="bx bx-check me-1"></i>
													선택됨
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					{/if}

					<!-- 다국어 제목 -->
					<div class="mb-4">
						<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
							<i class="bx bx-world me-2"></i>
							다국어 제목
						</h6>
						<div class="row">
							<div class="col-12">
								<div class="mb-3">
									<label for="subjectKo" class="form-label">
										한국어 제목 <span class="text-danger">*</span>
									</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectKo"
										value={editItem.subjectKo}
										on:input={(e) => handleInput("subjectKo", e.target.value)}
										placeholder="한국어 제목을 입력하세요"
									/>
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col-md-6">
								<div class="mb-3">
									<label for="subjectEn" class="form-label">영어 제목</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectEn"
										value={editItem.subjectEn}
										on:input={(e) => handleInput("subjectEn", e.target.value)}
										placeholder="English title"
									/>
								</div>
							</div>
							<div class="col-md-6">
								<div class="mb-3">
									<label for="subjectJa" class="form-label">일본어 제목</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectJa"
										value={editItem.subjectJa}
										on:input={(e) => handleInput("subjectJa", e.target.value)}
										placeholder="日本語のタイトル"
									/>
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col-md-6">
								<div class="mb-3">
									<label for="subjectZhCn" class="form-label">중국어 간체 제목</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectZhCn"
										value={editItem.subjectZhCn}
										on:input={(e) => handleInput("subjectZhCn", e.target.value)}
										placeholder="简体中文标题"
									/>
								</div>
							</div>
							<div class="col-md-6">
								<div class="mb-3">
									<label for="subjectZhTw" class="form-label">중국어 번체 제목</label>
									<input
										type="text"
										class="form-control form-control-sm"
										id="subjectZhTw"
										value={editItem.subjectZhTw}
										on:input={(e) => handleInput("subjectZhTw", e.target.value)}
										placeholder="繁體中文標題"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-sm btn-secondary" on:click={closeModal}>취소</button>
					<button type="button" class="btn btn-sm btn-primary" on:click={handleSave} disabled={isSaving}>
						{#if isSaving}
							<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
						{/if}
						{editItem.id ? "수정" : "등록"}
					</button>
				</div>
			</div>
		</div>
	</div>
{/if}

<!-- 사운드 소스 라이브러리 모달 -->
{#if showSourceLibrary}
	<div
		class="modal fade {showSourceLibrary ? 'show' : ''}"
		style="display: block;"
		tabindex="-1"
		aria-modal="true"
		role="dialog"
		transition:fade={{ duration: 200 }}
	>
		<div class="modal-dialog modal-dialog-centered" style="max-width: 95vw; width: 95vw; max-height: 95vh;">
			<div class="modal-content" style="height: 95vh;">
				<div class="modal-header">
					<h5 class="modal-title d-flex align-items-center">
						<i class="bx bx-music me-2"></i>
						사운드 소스 라이브러리
					</h5>
					<button type="button" class="btn-close" on:click={closeSourceLibrary}></button>
				</div>
				<div class="modal-body" style="flex: 1; overflow: hidden; display: flex; flex-direction: column;">
					<!-- 필터 및 검색 -->
					<div class="row mb-3" style="flex-shrink: 0;">
						<div class="col-md-4">
							<label class="form-label" for="sourceCategory">소스장르 선택</label>
							<select class="form-select form-select-sm" id="sourceCategory" bind:value={sourceActiveCategory}>
								<option value={null}>전체 소스장르</option>
								{#each sourceCategories as category}
									<option value={category.id}>
										{category.subject}
									</option>
								{/each}
							</select>
						</div>
						<div class="col-md-8">
							<label class="form-label" for="sourceSearch">제목 검색</label>
							<div class="input-group">
								<input
									type="text"
									class="form-control form-control-sm"
									id="sourceSearch"
									placeholder="사운드 소스 제목으로 검색..."
									bind:value={sourceSearchKeyword}
								/>
								<button class="btn btn-outline-secondary btn-sm" type="button">
									<i class="bx bx-search"></i>
								</button>
							</div>
						</div>
					</div>

					<!-- 사운드 소스 카드 목록 -->
					<div class="row" style="flex: 1; overflow-y: auto; min-height: 0;">
						{#if filteredSources.length === 0}
							<div class="col-12 text-center p-5">
								<i class="bx bx-info-circle fs-1 text-muted"></i>
								<p class="text-muted mt-3">조건에 맞는 사운드 소스가 없습니다.</p>
							</div>
						{:else}
							<div class="d-flex flex-wrap" style="gap: 0.8rem; padding: 0 1rem; height: fit-content;">
								{#each filteredSources as source, index (source.id)}
									<div style="width: 160px; margin-bottom: 0.8rem;" in:fade={{ duration: 300, delay: index * 20 }}>
										<div
											class="card h-100 source-card {editItem.idSoundSource === source.id ? 'selected' : ''}"
											style="cursor: pointer;"
											on:click={() => selectSource(source)}
										>
											<div class="position-relative">
												{#if source.musicImage}
													<div style="height: 90px; overflow: hidden;">
														<img
															src={source.musicImage}
															alt={source.subject}
															class="w-100 h-100 card-img-top"
															style="object-fit: cover; height: 90px;"
															on:error={handleImageError}
														/>
													</div>
												{:else}
													<div style="height: 90px; overflow: hidden;">
														<img
															src="/assets/img/nope-not-here.avif"
															alt="이미지 없음"
															style="width: 160px; height: 90px; object-fit: cover;"
														/>
													</div>
												{/if}

												<!-- 배지를 이미지 위에 겹쳐서 표시 -->
												<div class="position-absolute top-0 start-0 p-1 d-flex justify-content-between w-100">
													<span
														class="badge {source.isActive ? 'bg-primary text-white' : 'bg-secondary text-dark'}"
														style="font-size: 0.6rem; padding: 2px 6px;"
													>
														{source.isActive ? "노출" : "미노출"}
													</span>
													<span
														class="badge {getHRLevelBadgeColor(source.level)}"
														style="font-size: 0.6rem; padding: 2px 6px;"
													>
														{getHRLevelText(source.level)}
													</span>
												</div>

												<!-- 선택 표시 -->
												{#if editItem.idSoundSource === source.id}
													<div class="position-absolute top-0 end-0 p-1">
														<span class="badge bg-success" style="font-size: 0.6rem; padding: 2px 6px;">
															<i class="bx bx-check" style="font-size: 0.7rem;"></i>
															선택됨
														</span>
													</div>
												{/if}
											</div>
											<div class="card-body" style="padding: 6px;">
												<h6 class="card-title mb-0" style="font-size: 0.85rem; line-height: 1.2;">
													{source.subject}
												</h6>
												<p class="card-text mb-0" style="font-size: 0.75rem; margin-top: 3px;">
													소스장르: {sourceCategories.find((c) => c.id === source.idCategorySource)?.subject ||
														"소스장르 미지정"}
												</p>
											</div>
											<div class="card-footer d-flex justify-content-end align-items-center bg-light" style="padding: 4px 6px;">
												<div class="input-group justify-content-end">
													<!-- 재생 버튼 -->
													{#if source.fileName}
														<button
															class="btn btn-outline-primary btn-sm"
															on:click|stopPropagation={() => handleSourceAudioPlay(source)}
															title={sourcePlayingId === source.id && sourceIsPlaying ? "정지" : "재생"}
														>
															<i
																class="bx {sourcePlayingId === source.id && sourceIsPlaying ? 'bx-stop' : 'bx-play'}"
															></i>
														</button>
													{/if}
													<button class="btn btn-primary btn-sm" on:click|stopPropagation={() => selectSource(source)}>
														선택
													</button>
												</div>
											</div>
										</div>
									</div>
								{/each}
							</div>
						{/if}
					</div>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-secondary" on:click={closeSourceLibrary}>취소</button>
					<button type="button" class="btn btn-primary" disabled={!editItem.idSoundSource} on:click={closeSourceLibrary}>선택 완료</button>
				</div>
			</div>
		</div>
	</div>
{/if}

<!-- 상세보기 모달 제거됨 - 이제 편집 모달이 상세 기능을 담당 -->
